/* eslint-disable react-hooks/exhaustive-deps */
import React, { useLayoutEffect, useRef } from 'react';
import { StringMap, TOptions } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useService } from '../../../../../services/services';
import ProductList from './product-list';
import Watch from '../../../../../components/form/elements/watch';
import Select, {
  emptySelectOption,
} from '../../../../../components/form/elements/select';
import { map, switchMap } from 'rxjs/operators';
import { FormComponent } from '../../../../../components/form';
import Amount from '../../../../../components/form/elements/amount';
import Classifier from '../../../../../enums/classifier';
import Paginate from '../../../../../interfaces/paginate';
import Warehouse from '../../../../../interfaces/warehouse';
import asyncTypes from './async-types';
import asyncProducts from './async-products';
import asyncCategories from './async-categories';
import { IEmptyProduct } from '../hooks/use-product-list';
import Animal from '../../../../../interfaces/animal';
import ProductWarehouse from '../../../../../interfaces/product-warehouse';

const warehouseIsSelected = (selectedWarehouses: ProductWarehouse[], warehouse: string) => {
  let notSelected = true;
  selectedWarehouses.forEach((element) => {
    if (element.barn_id && element.barn_id.value === warehouse) {
      notSelected = false;
    }
  });
  return notSelected;
};

export const useFormProducts = (
  eventType: string = '',
  emptyProduct: IEmptyProduct,
  animals: Animal[] = []
) => {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback(
    (key: string, options?: string | TOptions<StringMap>) =>
      t(transKeyPrefix.concat(key), options),
    [t]
  );
  const { farmId, mode } = useParams<{
    farmId: string;
    mode?: string;
    id?: string;
  }>();

  const animalsLengthRef = useRef(animals.length);

  useLayoutEffect(() => {
    animalsLengthRef.current = animals.length;
  }, [animals]);

  const getName = (parentName: string, name: string) =>
    parentName.concat('.', name);

  const {
    classifierService,
    productService,
    warehouseService,
    remainderService,
  } = useService();

  const products: FormComponent = useMemo(
    function () {
      return ProductList({
        products: [
          {
            field: [
              Watch(
                Select('product_category_id', {
                  asyncOptions: asyncCategories({
                    classifierService,
                    eventType
                  }),
                  label: tp('animals.category'),
                  column: true,
                  clearable: false,
                  requiredShow: true,
                }),
                ({ value, setValue, parentName, getValues }) => {
                  const getName = (name: string) =>
                    parentName.concat('.', name);
                  const productType = getValues(getName('product_type_id'));
                  const product = getValues(getName('product_id'));

                  if (!!productType?.parent) {
                    if (productType.parent.value !== value[0].value) {
                      setValue(getName('product_type_id'), emptySelectOption);
                    }
                  }

                  if (!!product?.category) {
                    if (product.category.value !== value[0].value) {
                      setValue(getName('product_id'), emptySelectOption);
                    }
                  }
                }
              ),
              Watch(
                Select('product_type_id', {
                  asyncOptions: asyncTypes({
                    t,
                    classifierService,
                    eventType,
                  }),
                  label: tp('animals.type'),
                  cacheOn: ['product_category_id'],
                  column: true,
                  clearable: false,
                  requiredShow: true,
                }),
                ({ value, setValue, parentName, getValues }) => {
                  const getName = (name: string) =>
                    parentName.concat('.', name);
                  const category: any = getValues(
                    getName('product_category_id')
                  );
                  const product: any = getValues(getName('product_id'));

                  if (value[0]?.parent) {
                    if (category.value !== value[0].parent.id) {
                      setValue(getName('product_category_id'), value[0].parent);
                    }
                  }

                  if (!!product?.type) {
                    if (product.type.value !== value[0].value) {
                      setValue(getName('product_id'), emptySelectOption);
                    }
                  }
                }
              ),
              Watch(
                Select('product_id', {
                  asyncOptions: asyncProducts({
                    productService,
                    farmId,
                    classifierKeys: [
                      Classifier.PRODUCT_CATEGORIES_AND_TYPES,
                      Classifier.OBTAINED_PRODUCT_CATEGORIES_AND_TYPES,
                    ],
                    eventType
                  }),
                  label: tp('animals.products'),
                  cacheOn: (parentName => {
                    return [
                      `${parentName}.product_category_id`,
                      `${parentName}.product_type_id`,
                      'products.*.product_id',
                      'products',
                    ]
                  }),
                  column: true,
                  clearable: false,
                  creatable: true,
                  requiredShow: true,
                }),
                ({ value, setValue, parentName, getValues }) => {
                  const warehouses = getValues(`${parentName}.barns`);
                  warehouses.forEach((_, index) => {
                    setValue(`${parentName}.barns.${index}.barn_id`, emptySelectOption);
                    setValue(`${parentName}.barns.${index}.product_amount_for_one.amount`, '');
                    setValue(`${parentName}.barns.${index}.product_amount_for_one.unit`, emptySelectOption);
                  })

                  const getName = (name: string) =>
                    parentName.concat('.', name);
                  const category: any = getValues(
                    getName('product_category_id')
                  );
                  const type: any = getValues(getName('product_type_id'));

                  if (value[0]?.category) {
                    if (category.value !== value[0].category.value) {
                      setValue(
                        getName('product_category_id'),
                        value[0].category
                      );
                    }
                  }

                  if (value[0]?.type) {
                    if (type.value !== value[0].type.value) {
                      setValue(getName('product_type_id'), value[0].type);
                    }
                  }
                }
              ),
            ],
            name: 'products',
            title: tp('animals.product_title'),
          },
          {
            field: [
              Select('barn_id', {
                asyncOptions: (
                  value,
                  _l,
                  { page, getValues, parentName, setValue }
                ): any => {
                  const params = {
                    page,
                    search: value,
                  };
                  let product_parent = parentName
                    .split('.')
                    .slice(0, 2)
                    .join('.')
                    .concat('.');
                  const product = getValues(
                    getName(product_parent, 'product_id')
                  );
                  const product_category_id = getValues(
                    getName(product_parent, 'product_category_id.value')
                  );
                  const product_type_id = getValues(
                    getName(product_parent, 'product_type_id.value')
                  );

                  const path = parentName.split('.').slice(0, 3).join('.');
                  const barns = getValues(path);

                  const fillWarehouse = (response: Paginate<Warehouse>) => {
                    if (response.data?.length === 1) {
                      if (warehouseIsSelected(barns, response.data[0].id)) {
                        setValue(parentName.concat('.', 'barn_id'), {
                          label: response.data[0].name,
                          value: response.data[0].id,
                        });
                      }
                    }

                    return {
                      hasMore: response.last_page > response.current_page,
                      options: response.data
                        .map((v) => ({
                          label: v.name,
                          value: v.id,
                          category: product_category_id
                            ? product_category_id
                            : null,
                          type: product_type_id ? product_type_id : null,
                          product:
                            product?.value && !product?.__isNew__
                              ? product?.value
                              : null,
                        }))
                        .filter((v) => {
                          return warehouseIsSelected(barns, v.value);
                        }),
                      additional: { page: response.current_page + 1 },
                    };
                  };
                  const handleWarehouse = async (
                    response: Paginate<Warehouse>
                  ) => {
                    if (response.data.length === 1 && warehouseIsSelected(barns, response.data[0].id)) {
                      const v = response.data[0];
                      setValue(getName(parentName, 'barn_id'), {
                        label: v.name,
                        value: v.id,
                        category: product_category_id
                          ? product_category_id
                          : null,
                        type: product_type_id ? product_type_id : null,
                        product:
                          product?.value && !product?.__isNew__
                            ? product?.value
                            : null,
                      });
                    }
                    return fillWarehouse(response);
                  };

                  return warehouseService.index(farmId, params).pipe(
                    switchMap((warehouseResponse) =>
                      remainderService.index(farmId, {
                        page,
                        product_category_id: product_category_id,
                        product_type_id: product_type_id,
                        product_id: product?.value && !product?.__isNew__ ? product?.value : null,
                      }).pipe(
                        map((remainderResponse) => {
                          if (product?.value && !product?.__isNew__) {
                            const remainderWarehouses = remainderResponse?.data?.map(remainder => remainder.warehouse_id)
                            warehouseResponse.data = warehouseResponse.data.filter(warehouseData => remainderWarehouses?.includes(warehouseData.id))
                          }
                          return handleWarehouse(warehouseResponse)
                        })
                      )
                    )
                  );
                },
                label: tp('animals.warehouse'),
                column: true,
                clearable: false,
                requiredShow: true,
                cacheOn: (parentName) => {
                  let product_parent = parentName
                    .split('.')
                    .slice(0, 2)
                    .join('.')
                    .concat('.');

                  const warehousePath = parentName.split('.').slice(0, 3).join('.');

                  return [
                    product_parent.concat('product_category_id'),
                    product_parent.concat('product_type_id'),
                    product_parent.concat('product_id'),
                    `${warehousePath}.*.barn_id`,
                    warehousePath,
                  ];
                },
              }),
              Amount(
                {
                  amount: 'product_amount_for_one.amount',
                  unit: 'product_amount_for_one.unit',
                },
                {
                  options: [],
                  asyncOptions: (
                    _v,
                    _l,
                    { page, parentName, getValues, setValue }
                  ) => {
                    const getFieldPath = (name: string) =>
                      `${parentName}.${name}`;
                    const getBaseParentPath = (name: string) => {
                      const baseParentPath = parentName
                        .split('.')
                        .slice(0, 2)
                        .join('.');
                      return `${baseParentPath}.${name}`;
                    };

                    // Cache the getValues calls to avoid redundant calls
                    const category_id = getValues(
                      getBaseParentPath('product_category_id')
                    )?.value;
                    const type = getValues(
                      getBaseParentPath('product_type_id')
                    )?.value;
                    const product = getValues(getBaseParentPath('product_id'));
                    const product_id = product?.__isNew__ ? '' : product?.value;
                    const barn_id = getValues(getFieldPath('barn_id'))?.value;
                    const barns = getValues(getBaseParentPath('barns'));

                    if (product?.__isNew__) {
                      setValue(
                        getFieldPath('product_amount_for_one.amount'),
                        String(0)
                      );
                      setValue(
                        getFieldPath('product_amount_for_one.unit'),
                        emptySelectOption
                      );
                      return;
                    }

                    // API call to retrieve data
                    return remainderService
                      .index(farmId, {
                        page,
                        product_category_id: category_id,
                        product_type_id: type,
                        product_id: product_id,
                        warehouse_id: barn_id,
                      })
                      .pipe(
                        map((response) => {
                          const responseData =
                            response.data[0] || ({} as any);
                          const value = responseData.unit || '';
                          const unitObject = { value, label: value };

                          // Update form fields based on response data
                          if (product.value !== '') {
                            if (response.data.length === 1) {
                              const amount = responseData.amount || '';
                              if (!barn_id && warehouseIsSelected(barns, responseData.warehouse_id)) {
                                setValue(
                                  getFieldPath('barn_id'),
                                  {
                                    value: responseData.warehouse_id,
                                    label: responseData.warehouse.name
                                  }
                                );
                                setValue(
                                  getFieldPath('product_amount_for_one.amount'),
                                  String(amount)
                                );
                                setValue(
                                  getFieldPath('product_amount_for_one.unit'),
                                  unitObject
                                );
                                setValue(
                                  getFieldPath('product_unit'),
                                  unitObject
                                );
                                setValue(
                                  getFieldPath('amount_for_one_unit'),
                                  unitObject
                                );
                              }
                            } else {
                              setValue(
                                getFieldPath('product_amount_for_one.amount'),
                                ''
                              );

                              if (unitObject.value) {
                                setValue(
                                  getFieldPath('product_amount_for_one.unit'),
                                  unitObject
                                );
                                setValue(
                                  getFieldPath('product_unit'),
                                  unitObject
                                );
                                setValue(
                                  getFieldPath('amount_for_one_unit'),
                                  unitObject
                                );
                              } else {
                                const productUnit = getValues(
                                  getBaseParentPath('product_unit')
                                );
                                if (!!productUnit.value) {
                                  setValue(
                                    getFieldPath('product_unit'),
                                    productUnit
                                  );
                                  setValue(
                                    getFieldPath('amount_for_one_unit'),
                                    productUnit
                                  );
                                }
                              }
                            }
                          }

                          return {
                            hasMore: response.last_page > response.current_page,
                            options: [
                              {
                                value: responseData.unit,
                                label: responseData.unit,
                              },
                            ],
                            additional: { page: response.current_page + 1 },
                          };
                        })
                      );
                  },
                  cacheOn: (parentName) => {
                    const baseParentPath = parentName
                      .split('.')
                      .slice(0, 2)
                      .join('.');
                    const createPath = (suffix: string) =>
                      `${baseParentPath}.${suffix}.value`;

                    return [
                      createPath('product_category_id'),
                      createPath('product_type_id'),
                      createPath('product_id'),
                      `${parentName}.barn_id`,
                      baseParentPath + '.barns',
                    ];
                  },
                  label: tp('animals.product_amount_total'),
                  column: true,
                  onDisabled: 'both',
                  disableAmount: true,
                  disableUnit: true,
                }
              ),

              (rest) => {
                return (
                  <>
                    <AmountForOne
                      tp={tp}
                      animalsLength={animalsLengthRef.current}
                      {...rest}
                    />
                  </>
                );
              },

              (rest) => {
                return (
                  <>
                    <ProductAmountForOne
                      tp={tp}
                      animalsLength={animalsLengthRef.current}
                      {...rest}
                    />
                  </>
                );
              },
            ],
            name: (index) => 'products.' + index + '.barns',
            title: tp('animals.warehouse_title'),
          },
        ],
        emptyProduct,
      });
    },
    [mode]
  );

  return {
    products,
  };
};

const AmountForOne = ({ tp, animalsLength, ...rest }) => {
  const prevAmountTotalRef = useRef(0);
  const AmountForOneComponent = useMemo(
    () =>
      Watch(
        Amount(
          { amount: 'amount_for_one', unit: 'amount_for_one_unit' },
          {
            options: [],
            asyncOptions: async (
              value,
              loadedOptions,
              { parentName, getValues, setValue }
            ) => {
              const getFullFieldName = (name: string) =>
                parentName.concat('.', name);
              const baseParentPath = parentName
                .split('.')
                .slice(0, 2)
                .join('.');

              const unitForOne = getValues(getFullFieldName('product_amount_for_one.unit'));

              let options = [];

              if (unitForOne?.value) {
                options = [
                  {
                    label: unitForOne.value,
                    value: unitForOne.value,
                  },
                ];

                setValue(getFullFieldName('amount_for_one_unit'), {
                  value: unitForOne?.value,
                  label: unitForOne?.value,
                });
              } else {
                const typeUnits = getValues(
                  baseParentPath.concat('.', 'product_type_id.units')
                );
                const productUnit = getValues(
                  baseParentPath.concat('.', 'product_id.unit')
                );

                options = productUnit?.value
                  ? [productUnit]
                  : typeUnits?.map((v: string) => ({
                      label: v,
                      value: v,
                    })) ?? [];

                if (!!options[0]?.value) {
                  setValue(getFullFieldName('amount_for_one_unit'), {
                    value: options[0]?.value,
                    label: options[0]?.value,
                  });
                }
              }

              return {
                hasMore: false,
                options,
                additional: {},
              };
            },
            label: tp('events.quantity_per_animal'),
            cacheOn: (parentName) => {
              const baseParentPath = parentName
                .split('.')
                .slice(0, 2)
                .join('.');
              const createPath = (suffix: string) =>
                `${baseParentPath}.${suffix}.value`;

              return [
                createPath('product_id'),
                `${parentName}.barn_id`,
                `${parentName}.product_amount_for_one.amount`,
              ];
            },
            column: true,
            onDisabled: 'both',
            disableUnit: true,
            disableAmount: String(animalsLength) === '0',
          }
        ),
        ({ setValue, value, parentName, setError, getValues }) => {
          const getFullFieldName = (name: string) =>
            parentName.concat('.', name);
          const animals = getValues('animals');
          const days = getValues('days');
          const prevProductAmountTotal = getValues(
            getFullFieldName('product_amount_total')
          );

          const productAmountTotal = !!days
            ? value[0] * animals.length * days
            : value[0] * animals.length;

          if (prevAmountTotalRef.current !== productAmountTotal) {
            setValue(
              getFullFieldName('product_amount_total'),
              isNaN(productAmountTotal) ? '0' : String(productAmountTotal), {
                shouldValidate: true,
              }
            );

            prevAmountTotalRef.current = Number(prevProductAmountTotal);
          }

          setValue(
            getFullFieldName('product_amount_total'),
            isNaN(productAmountTotal) ? '0' : String(productAmountTotal), {
              shouldValidate: true,
            }
          );
          setError(getFullFieldName('product_amount_total'), null);
        }
      ),
    [animalsLength]
  );

  return <AmountForOneComponent {...rest} />;
};

const ProductAmountForOne = ({ tp, animalsLength, ...rest }) => {
  const prevAmountForOneRef = useRef(0);
  const AmountForTotal = useMemo(
    () =>
      Watch(
        Amount(
          { amount: 'product_amount_total', unit: 'product_unit' },
          {
            options: [],
            asyncOptions: async (
              value,
              loadedOptions,
              { parentName, getValues, setValue }
            ) => {
              const getFullFieldName = (name: string) =>
                parentName.concat('.', name);
              const baseParentPath = parentName
                .split('.')
                .slice(0, 2)
                .join('.');

              const unitForOne = getValues(getFullFieldName('product_amount_for_one.unit'));

              let options = [];

              if (unitForOne?.value) {
                options = [
                  {
                    label: unitForOne.value,
                    value: unitForOne.value,
                  },
                ];

                setValue(getFullFieldName('product_unit'), {
                  value: unitForOne?.value,
                  label: unitForOne?.value,
                });
              } else {
                const typeUnits = getValues(
                  baseParentPath.concat('.', 'product_type_id.units')
                );
                const productUnit = getValues(
                  baseParentPath.concat('.', 'product_id.unit')
                );

                options = productUnit?.value
                  ? [productUnit]
                  : typeUnits?.map((v: string) => ({
                      label: v,
                      value: v,
                    })) ?? [];

                if (!!options[0]?.value) {
                  setValue(getFullFieldName('product_unit'), {
                    value: options[0]?.value,
                    label: options[0]?.value,
                  });
                }
              }

              return {
                hasMore: false,
                options,
                additional: {},
              };
            },
            cacheOn: (parentName) => {
              const baseParentPath = parentName
                .split('.')
                .slice(0, 2)
                .join('.');
              const createPath = (suffix: string) =>
                `${baseParentPath}.${suffix}.value`;

              return [
                createPath('product_id'),
                `${parentName}.barn_id`,
                `${parentName}.product_amount_for_one.amount`,
              ];
            },
            label: tp('events.separation.product_amount_total'),
            column: true,
            onDisabled: 'amount',
            disableUnit: false,
            disableAmount: String(animalsLength) === '0',
            requiredShow: true,
          }
        ),
        ({ setValue, value, parentName, getValues }) => {
          const getFullFieldName = (name: string) =>
            parentName.concat('.', name);
          const animals = getValues('animals');
          const days = getValues('days');
          const amountForOne = !!days
            ? value[0] / (animals.length * days)
            : value[0] / animals.length;

          if (prevAmountForOneRef.current !== amountForOne) {
            setValue(getFullFieldName('amount_for_one'), String(amountForOne), {
              shouldValidate: true,
            });

            prevAmountForOneRef.current = Number(amountForOne);
          }

          setValue(getFullFieldName('amount_for_one_unit'), value[1], {
            shouldValidate: true,
          });
          setValue(getFullFieldName('product_amount_for_one.unit'), value[1], {
            shouldValidate: true,
          });
        }
      ),
    [animalsLength]
  );

  return <AmountForTotal {...rest} />;
};
